import { supabase } from '../lib/supabase';
import type { EntryItem, MealType } from './entries';
import { retryAfterResume } from '../lib/resumeFetch';

// השרת שומר עד 14ש' לכל ניסיון מודל/מפתח (functions/api/_gemini.ts) ומנסה כמה שילובים —
// בלי טיימאאוט בצד לקוח, תקלת רשת אמיתית (לא רק עומס בגוגל) יכולה לתקוע את המסך ללא הגבלה.
// 45ש' נותן מרווח נדיב לשרשרת המלאה בלי לתת לבקשה תקועה להימשך לנצח.
const AI_TIMEOUT_MS = 45000;
// ⚠️ ניתוח תמונות אורך לגיטימית 20–40 שניות (נמדד 16.09.2026: ארבע תמונות = 33 ו-72 שניות),
// והלקוח היה מוותר ב-45 — כלומר המשתמשת ראתה "לקח יותר מדי זמן" על בקשה שעדיין רצה.
// התקציב כאן חייב להיות גדול מהתקרה בשרת (75ש' ב-nutri.ts), אחרת הלקוח חותך ראשון.
const AI_PHOTO_TIMEOUT_MS = 90000;

/** ניסיון בודד. נקטע (יציאה מהאפליקציה) → retryAfterResume שולח שוב כשהיא חוזרת. */
function fetchWithTimeout(url: string, init: RequestInit, timeoutMs = AI_TIMEOUT_MS): Promise<Response> {
  return retryAfterResume(
    () => fetchOnce(url, init, timeoutMs),
    (e) => e instanceof Error && (e.message === 'network' || e.message === 'ai_timeout'),
  );
}

async function fetchOnce(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(url, { ...init, signal: ctrl.signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw new Error('ai_timeout');
    // fetch שנזרק בלי תשובה = הבקשה לא הגיעה לשרת (אין קליטה / הרשת נפלה). עד 28.09.2026
    // זה התמפה להודעה הגנרית, שרמזה שהתמונה היא הבעיה.
    throw new Error('network');
  } finally {
    clearTimeout(timer);
  }
  return res;
}

/** גוף התשובה. שרת שהחזיר משהו שאינו JSON (דף שגיאה של Cloudflare) — תקלה אצלנו, לא אצל
 *  המשתמשת; ממופה לקוד מפורש במקום SyntaxError שהיה נופל להודעה הגנרית. */
async function readJson<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    return { ok: false, error: `http_${res.status}` } as T;
  }
}

/**
 * המודל ענה תשובה ענייניית — רק בלי פריטים ("לא נראה מזון בתמונה", "איזה סוג גבינה?").
 * זו אינה תקלה אלא שאלה, ולכן היא נושאת את הטקסט עצמה במקום להתמפות להודעה גנרית.
 */
export class AiClarifyError extends Error {
  readonly clarify: string;
  constructor(clarify: string) {
    super('need_clarify');
    this.name = 'AiClarifyError';
    this.clarify = clarify;
  }
}

/** תמונה שלא תרמה אף פריט לניתוח — מהמודל או מסינון השרת. index הוא 1-based. */
export interface SkippedImage {
  index: number;
  reason: string;
}

export interface ParsedMealResult {
  meal: {
    meal_type: MealType | null;
    items: EntryItem[];
    totals: { calories: number; protein_g: number; carbs_g: number; fat_g: number };
    clarify: string | null;
    /** ⚠️ תמונות שהושמטו מהחישוב. בלי להציג את זה, המשתמשת בטוחה שכל מה שצילמה נספר. */
    skipped?: SkippedImage[];
    /** התמלול של הקלטה קולית — מוצג למשתמשת כדי שתראה מה נשמע */
    heard?: string | null;
  };
}

async function postNutri(payload: Record<string, unknown>, timeoutMs?: number): Promise<ParsedMealResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const res = await fetchWithTimeout('/api/nutri', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  }, timeoutMs);
  const body = await readJson<
    { ok: boolean; error?: string; clarify?: string; meal?: ParsedMealResult['meal'] }>(res);
  if (body.error === 'need_clarify' && body.clarify) throw new AiClarifyError(body.clarify);
  if (!res.ok || !body.ok || !body.meal) throw new Error(body.error ?? `http_${res.status}`);
  return { meal: body.meal };
}

/** פירוק תיאור ארוחה בעברית דרך ‎/api/nutri (Gemini + מאגר המזון הישראלי) */
export function parseMeal(text: string): Promise<ParsedMealResult> {
  return postNutri({ action: 'parse_meal', text });
}

/** תמונה בודדת לניתוח — עם רמז-טקסט משלה (נשלחת יחד עם עוד תמונות כארוחה אחת) */
export interface MealPhoto {
  base64: string;
  mime: string;
  hint?: string;
}

/** פירוק ארוחה מתמונה אחת או כמה תמונות יחד דרך ‎/api/nutri (Gemini מולטימודאלי) */
export function parseMealPhoto(photos: MealPhoto[]): Promise<ParsedMealResult> {
  return postNutri({
    action: 'parse_photo',
    images: photos.map((p) => ({ image_base64: p.base64, mime: p.mime, hint: p.hint })),
  }, AI_PHOTO_TIMEOUT_MS);
}

/** תמלול בלבד — לשדה הרמז במסך הצילום. */
export async function transcribeAudio(audioBase64: string, mime = 'audio/wav'): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const res = await fetchWithTimeout('/api/nutri', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ action: 'transcribe', audio: audioBase64, mime }),
  });
  const body = await readJson<{ ok: boolean; error?: string; clarify?: string; text?: string }>(res);
  if (body.error === 'need_clarify' && body.clarify) throw new AiClarifyError(body.clarify);
  if (!res.ok || !body.ok || !body.text) throw new Error(body.error ?? `http_${res.status}`);
  return body.text;
}

/** ניתוח הקלטה קולית: התמלול והפירוק בקריאה אחת. ‏WAV בבסיס 64 (ראו lib/audioRec). */
export function parseMealVoice(audioBase64: string, mime = 'audio/wav'): Promise<ParsedMealResult> {
  return postNutri({ action: 'parse_meal', audio: audioBase64, mime }, AI_PHOTO_TIMEOUT_MS);
}

/** תיקון פירוק קיים לפי הערת המשתמש (למשל "זה קוסקוס לא אורז, בלי רוטב") */
export function refineMeal(items: EntryItem[], correction: string): Promise<ParsedMealResult> {
  return postNutri({ action: 'refine_meal', items, text: correction });
}

/** תוצאת קריאת תווית מוצר — ערכים מנורמלים ל-100 ג' + יחידה אם זוהתה */
export interface FoodLabel {
  name_he: string;
  calories_100g: number;
  protein_100g: number | null;
  carbs_100g: number | null;
  fat_100g: number | null;
  unit_name: string;
  unit_grams: number | null;
  clarify: string | null;
}

/** קריאת תווית/טבלת ערכים תזונתיים מצילום — לטופס "מאכל שלי" */
export async function parseFoodLabel(imageBase64: string, mime: string): Promise<FoodLabel> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const res = await fetchWithTimeout('/api/nutri', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action: 'parse_label', image_base64: imageBase64, mime }),
  }, AI_PHOTO_TIMEOUT_MS);
  const body = await readJson<
    { ok: boolean; error?: string; clarify?: string; label?: FoodLabel }>(res);
  if (body.error === 'need_clarify' && body.clarify) throw new AiClarifyError(body.clarify);
  if (!res.ok || !body.ok || !body.label) throw new Error(body.error ?? `http_${res.status}`);
  return body.label;
}
